import type { ReactElement } from 'react'

import { useSearchParams } from 'react-router-dom'
import { ActivationFlow } from '../pages/signin/br/ActivationFlow'
import type { BrActivationMode } from '../types/brActivation'
import { updatePlatformHeader } from '../services/api/auth'
import { setPlatformForLogs } from '../services/datadog'
import { redirectToUrl } from '../utils/location'
import { nativeAuthRedirect } from '../utils/nativeAuthRedirect'
import { activationRedirectUrl } from '../utils/originRedirect'
import { QueryParameters } from '../utils/queryParameters'
import type { Platform } from './BaseSignInRoute'

/**
 * `/signin/activation/br` — entry point for the Brazilian user **activation** funnel.
 * The whole flow is server-driven and gated by a short-lived scoped JWT stored in a
 * JS-readable `swile:activation` cookie and forwarded as `Authorization: Bearer <token>`
 * (no CSRF needed — Bearer replaces the double-submit pattern used by the main app).
 * This route just mounts the orchestrator, which resumes via `GET /signin/activation/br/state`.
 *
 * Unlike login (separate `/signin/ios|android` routes), the funnel is a single route, so
 * the originating platform travels in the `platform` query param set by the `/signin`
 * on-ramp. We mirror the SignIn routes by setting the platform header + logs to the real
 * platform here (defaulting to `web`) and by injecting the post-finalize "Continue"
 * strategy: web redirects to the employee app, native replays the OAuth authorization-code
 * callback that closes the iOS/Android auth webview (same `nativeAuthRedirect` helper as
 * `SignInIOSRoute`/`SignInAndroidRoute`). This keeps `ActivationFlow` platform-agnostic.
 */
export const BrActivationRoute = (): ReactElement => {
  const [searchParams] = useSearchParams()
  const platform: Platform = searchParams.get(QueryParameters.PLATFORM) || 'web'

  updatePlatformHeader(platform)
  setPlatformForLogs(platform)

  const isNative = platform === 'ios' || platform === 'android'

  // Post-finalize "Continue": web redirects to the FE-owned destination, native replays
  // the OAuth authorization-code exchange (falls back to web if the finalize token is missing).
  const onComplete = (accessToken: string | null, _mode: BrActivationMode | null): void | Promise<void> => {
    if (isNative && accessToken) {
      const redirectTo = searchParams.get(QueryParameters.REDIRECT_TO) ?? ''
      return nativeAuthRedirect(accessToken, redirectTo)
    }
    redirectToUrl(activationRedirectUrl(searchParams))
  }

  return <ActivationFlow onComplete={onComplete} />
}
